import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, LogOut, Trash2, Star } from "lucide-react";
import { toast } from "sonner";
import PageMeta from "@/components/PageMeta";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useWatchlist, useRecentlyViewed } from "@/hooks/useUserTokens";
import { supabase } from "@/integrations/supabase/client";
import { SwyftxReportSettings } from "@/components/SwyftxReportSettings";

const Account = () => {
  const { user, isLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const { items: pinned } = useWatchlist();
  const { data: recent } = useRecentlyViewed();
  const [deleting, setDeleting] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-16 max-w-md text-center space-y-4">
          <h1 className="type-page-title">Account</h1>
          <p className="text-muted-foreground">You're not signed in.</p>
          <Button asChild>
            <Link to="/auth">Sign in</Link>
          </Button>
        </div>
      </div>
    );
  }

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-account");
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Delete failed");
      await supabase.auth.signOut();
      toast.success("Your account and saved data have been deleted.");
      navigate("/");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't delete your account.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PageMeta
        title="Your account | GoToCoins"
        description="Manage your GoToCoins account, saved watchlist and stored data."
        path="/account"
      />
      <div className="container mx-auto px-4 py-10 max-w-2xl space-y-6">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <h1 className="type-page-title">Your account</h1>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Signed in as</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="type-data-sm break-all">{user.email}</p>
            <Button variant="outline" onClick={handleSignOut} className="gap-2">
              <LogOut className="w-4 h-4" /> Sign out
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">What we store about you</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              Just your email address, the coins you pin, and the coins you've recently opened, and — only if you add one — your encrypted Swyftx read-only key and your daily reports.
            </p>
            <div className="flex items-center gap-4 text-foreground">
              <span className="flex items-center gap-1.5 type-data-sm">
                <Star className="w-4 h-4 text-warning" /> {pinned.length} pinned
              </span>
              <span className="type-data-sm">{recent?.length ?? 0} recently viewed</span>
            </div>
          </CardContent>
        </Card>

        <SwyftxReportSettings />

        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle className="text-base text-destructive">Delete my account and data</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This permanently removes your login, your watchlist and your recently viewed list.
              It can't be undone.
            </p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="gap-2" disabled={deleting}>
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Delete my account and data
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete your account?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Your login, watchlist and recently viewed list will be permanently deleted.
                    You can keep using the site without an account.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
                    Yes, delete everything
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Account;
